
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/
import React, { useState, useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { XIcon, CheckCircleIcon, LockIcon, PayPalIcon, TwintIcon, SofortIcon, KlarnaIcon } from './icons';
import { useTranslation } from '../i18n/useTranslation';
import Spinner from './Spinner';
import { processMembershipPayment } from '@shared/services/paymentService';
import { useAppContext } from '../AppContext';
import { PaymentMethod } from '../types';
import { DiscountService } from '../services/discountService';
import { useLocalizedPrice } from '../utils/priceUtils';

interface PaymentModalProps {
    isOpen: boolean;
    onClose: () => void;
    tierId: string;
    tierName: string;
    price: string;
    originalPriceEUR?: number;
    onSuccess: () => void;
}


const PaymentModal: React.FC<PaymentModalProps> = ({ isOpen, onClose, tierId, tierName, price, originalPriceEUR, onSuccess }) => {
    const { convertAndFormat } = useLocalizedPrice();
    const [originalPriceFormatted, setOriginalPriceFormatted] = useState<string | null>(null);

    useEffect(() => {
        if (isOpen && originalPriceEUR) {
            convertAndFormat(originalPriceEUR).then(setOriginalPriceFormatted);
        }
    }, [isOpen, originalPriceEUR, convertAndFormat]);
    const { t } = useTranslation();
    const { userProfile } = useAppContext();
    const [isProcessing, setIsProcessing] = useState(false);
    const [paymentSuccess, setPaymentSuccess] = useState(false);
    const [paymentError, setPaymentError] = useState<string | null>(null);
    const [selectedMethod, setSelectedMethod] = useState<PaymentMethod>('card');

    // Form State
    const [cardNumber, setCardNumber] = useState('');
    const [expiry, setExpiry] = useState('');
    const [cvc, setCvc] = useState('');
    const [cardName, setCardName] = useState('');
    const [payEmail, setPayEmail] = useState(userProfile.email || '');
    const [payPhone, setPayPhone] = useState(userProfile.measurements?.phone as string || '');

    useEffect(() => {
        if (isOpen) {
            setPaymentSuccess(false);
            setPaymentError(null);
            setIsProcessing(false);
            setCardNumber('');
            setExpiry('');
            setCvc('');
            setCardName('');
            setPayEmail(userProfile.email || '');
            setPayPhone(userProfile.measurements?.phone as string || '');
            setSelectedMethod('card');
        }
    }, [isOpen, userProfile.email, userProfile.measurements?.phone]);

    const handleSubmit = async (e?: React.FormEvent) => {
        if (e) e.preventDefault();

        const { validateCardNumber, validateExpiry, validateCVV, validateEmail, validatePhone } = await import('../../Shared/lib/paymentValidation');

        if (selectedMethod === 'card') {
            if (!cardNumber || !expiry || !cvc || !cardName) return;

            if (!validateCardNumber(cardNumber)) {
                setPaymentError(t('payment_error_invalid_card'));
                return;
            }
            if (!validateExpiry(expiry)) {
                setPaymentError(t('payment_error_invalid_expiry'));
                return;
            }
            if (!validateCVV(cvc)) {
                setPaymentError(t('payment_error_invalid_cvc'));
                return;
            }
        } else if (selectedMethod === 'paypal' || selectedMethod === 'klarna') {
            if (!payEmail) {
                setPaymentError(t('payment_error_email_required'));
                return;
            }
            if (!validateEmail(payEmail)) {
                setPaymentError(t('payment_error_invalid_email'));
                return;
            }
        } else if (selectedMethod === 'twint') {
            if (!payPhone) {
                setPaymentError(t('payment_error_phone_required'));
                return;
            }
            if (!validatePhone(payPhone)) {
                setPaymentError(t('payment_error_invalid_phone'));
                return;
            }
        }

        setIsProcessing(true);
        setPaymentError(null);
        try {
            await processMembershipPayment(
                price,
                tierId,
                {
                    method: selectedMethod,
                    cardNumber: selectedMethod === 'card' ? cardNumber : undefined,
                    cardName: selectedMethod === 'card' ? cardName : undefined,
                    expiryDate: selectedMethod === 'card' ? expiry : undefined,
                    cvv: selectedMethod === 'card' ? cvc : undefined,
                    email: (selectedMethod === 'paypal' || selectedMethod === 'klarna') ? payEmail : undefined,
                    phone: selectedMethod === 'twint' ? payPhone : undefined
                },
                selectedMethod,
                userProfile.email,
                userProfile.measurements?.name as string || undefined
            );

            setPaymentSuccess(true);
            setTimeout(() => {
                onSuccess();
                onClose();
            }, 2000);
        } catch (error) {
            console.error("Payment failed", error);
            const message = error instanceof Error ? error.message : t('payment_failed_message');

            if (message === 'Invalid card number.') setPaymentError(t('payment_error_invalid_card'));
            else if (message === 'Invalid or expired date.') setPaymentError(t('payment_error_invalid_expiry'));
            else if (message === 'Invalid CVV.') setPaymentError(t('payment_error_invalid_cvc'));
            else setPaymentError(t('payment_failed_message'));

            setIsProcessing(false);
        }
    };

    const methods: { id: PaymentMethod; label: string; icon: React.FC<any> }[] = [
        { id: 'card', label: t('payment_method_card') || 'Credit Card', icon: LockIcon },
        { id: 'paypal', label: t('payment_method_paypal') || 'PayPal', icon: PayPalIcon },
        { id: 'klarna', label: t('payment_method_klarna') || 'Klarna', icon: KlarnaIcon },
        { id: 'twint', label: t('payment_method_twint') || 'Twint', icon: TwintIcon },
        { id: 'sofort', label: t('payment_method_sofort') || 'Sofort', icon: SofortIcon },
    ];

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                >
                    <motion.div
                        initial={{ scale: 0.95, y: 20 }}
                        animate={{ scale: 1, y: 0 }}
                        exit={{ scale: 0.95, y: 20 }}
                        className="relative bg-white dark:bg-gray-800 rounded-2xl w-full max-w-md flex flex-col shadow-2xl border border-gray-200 dark:border-gray-700 max-h-[90vh] overflow-y-auto"
                    >
                        <button onClick={onClose} className="absolute top-4 right-4 p-1 rounded-full text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 z-10" title={t('common_close') || 'Close'}>
                            <XIcon className="w-6 h-6" />
                        </button>

                        <div className="p-8">
                            <div className="text-center mb-6">
                                <h2 className="text-2xl font-serif font-bold text-gray-900 dark:text-white mb-2">Upgrade to {tierName}</h2>
                                <p className="text-gray-500 dark:text-gray-400">Complete your secure payment.</p>
                                <div className="mt-4 inline-block bg-indigo-50 dark:bg-indigo-900/30 px-4 py-2 rounded-lg">
                                    <div className="flex flex-col items-center">
                                        {originalPriceFormatted && originalPriceFormatted !== price && (
                                            <span className="text-xs line-through text-gray-400 dark:text-gray-500 mb-1">{originalPriceFormatted}</span>
                                        )}
                                        <span className="text-xl font-bold text-indigo-600 dark:text-indigo-400">{price}</span>
                                    </div>
                                </div>
                            </div>

                            {paymentSuccess ? (
                                <div className="flex flex-col items-center justify-center py-8 animate-fade-in">
                                    <CheckCircleIcon className="w-16 h-16 text-green-500 mb-4" />
                                    <h3 className="text-xl font-bold text-gray-900 dark:text-white">{t('payment_success_title')}</h3>
                                    <p className="text-gray-500 dark:text-gray-400 mt-2 text-center">{t('payment_success_message')}</p>
                                </div>
                            ) : (
                                <div className="space-y-6">
                                    {paymentError && (
                                        <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-800 rounded-xl text-red-600 dark:text-red-400 text-sm animate-shake">
                                            <div className="font-bold mb-1">{t('payment_failed_title')}</div>
                                            {paymentError}
                                        </div>
                                    )}
                                    {/* Method Selection */}
                                    <div className="grid grid-cols-2 gap-3">
                                        {methods.map((method) => (
                                            <button
                                                key={method.id}
                                                onClick={() => setSelectedMethod(method.id)}
                                                className={`flex flex-col items-center justify-center p-3 rounded-xl border-2 transition-all ${selectedMethod === method.id
                                                    ? 'border-indigo-600 bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400'
                                                    : 'border-gray-100 dark:border-gray-700 hover:border-gray-200 dark:hover:border-gray-600 text-gray-500'
                                                    }`}
                                            >
                                                <method.icon className="w-6 h-6 mb-2" />
                                                <span className="text-xs font-medium text-center">{method.label}</span>
                                            </button>
                                        ))}
                                    </div>

                                    {selectedMethod === 'card' ? (
                                        <form onSubmit={handleSubmit} className="space-y-4">
                                            <div>
                                                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 uppercase mb-1">{t('payment_cardholder_name') || 'Cardholder Name'}</label>
                                                <input
                                                    type="text"
                                                    value={cardName}
                                                    onChange={e => setCardName(e.target.value)}
                                                    placeholder="Name on Card"
                                                    className="w-full p-3 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
                                                    required
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 uppercase mb-1">{t('payment_card_number') || 'Card Number'}</label>
                                                <div className="relative">
                                                    <input
                                                        type="text"
                                                        value={cardNumber}
                                                        onChange={e => setCardNumber(e.target.value.replace(/\D/g, '').slice(0, 16))}
                                                        placeholder="0000 0000 0000 0000"
                                                        className="w-full p-3 pl-10 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
                                                        required
                                                    />
                                                    <LockIcon className="absolute left-3 top-3.5 w-4 h-4 text-gray-400" />
                                                </div>
                                            </div>
                                            <div className="grid grid-cols-2 gap-4">
                                                <div>
                                                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 uppercase mb-1">{t('payment_expiry') || 'Expiry'}</label>
                                                    <input
                                                        type="text"
                                                        value={expiry}
                                                        onChange={e => setExpiry(e.target.value)}
                                                        placeholder="MM/YY"
                                                        className="w-full p-3 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
                                                        required
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 uppercase mb-1">{t('payment_cvc') || 'CVC'}</label>
                                                    <input
                                                        type="text"
                                                        value={cvc}
                                                        onChange={e => setCvc(e.target.value.replace(/\D/g, '').slice(0, 3))}
                                                        placeholder="123"
                                                        className="w-full p-3 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
                                                        required
                                                    />
                                                </div>
                                            </div>

                                            <button
                                                type="submit"
                                                disabled={isProcessing}
                                                className="w-full bg-gray-900 text-white font-bold py-3 rounded-lg hover:bg-gray-800 dark:bg-indigo-600 dark:hover:bg-indigo-500 transition-colors flex items-center justify-center mt-4"
                                            >
                                                {isProcessing ? <Spinner className="w-5 h-5 text-white" /> : `Pay ${price}`}
                                            </button>
                                        </form>
                                    ) : (
                                        <div className="flex flex-col items-center justify-center py-6 space-y-4">
                                            <p className="text-gray-600 dark:text-gray-300 text-center">
                                                {t('payment_continue_to', { method: methods.find(m => m.id === selectedMethod)?.label })}
                                            </p>

                                            {(selectedMethod === 'paypal' || selectedMethod === 'klarna') && (
                                                <div className="w-full">
                                                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 uppercase mb-1">{t('payment_email_label') || 'Confirm Email Address'}</label>
                                                    <input
                                                        type="email"
                                                        value={payEmail}
                                                        onChange={e => setPayEmail(e.target.value)}
                                                        placeholder="confirm@gmail.com"
                                                        className="w-full p-3 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
                                                        required
                                                    />
                                                </div>
                                            )}

                                            {selectedMethod === 'twint' && (
                                                <div className="w-full">
                                                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 uppercase mb-1">{t('payment_mobile_label') || 'Mobile Number'}</label>
                                                    <input
                                                        type="tel"
                                                        value={payPhone}
                                                        onChange={e => setPayPhone(e.target.value)}
                                                        placeholder="+41 7x xxx xx xx"
                                                        className="w-full p-3 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
                                                        required
                                                    />
                                                </div>
                                            )}

                                            <button
                                                onClick={() => handleSubmit()}
                                                disabled={isProcessing}
                                                className="w-full bg-indigo-600 text-white font-bold py-3 rounded-lg hover:bg-indigo-700 transition-colors flex items-center justify-center"
                                            >
                                                {isProcessing ? <Spinner className="w-5 h-5 text-white" /> : 'Proceed to Payment'}
                                            </button>
                                        </div>
                                    )}

                                    <p className="text-xs text-center text-gray-400 mt-4 flex items-center justify-center gap-1">
                                        <LockIcon className="w-3 h-3" /> Secure Payment Processing
                                    </p>
                                </div>
                            )}
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default PaymentModal;
